import React, { useState, useEffect } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Autocomplete, TextField, OutlinedInput, Chip, Grid } from '@mui/material';
import { useQuery, useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import { SimpleDatePicker } from 'components/form/FormInputDate';
import { ContactOverviewReportFiltersInput, } from '__generated__/graphql';
import { allCountriesSorted } from 'languages';
import { REPORT_LIST_PUBLIC_COMPANIES_QUERY } from '../../gql';
import { LIST_KEY_ACCOUNT_MANAGERS_QUERY, LIST_CONTACT_SUBJECTS_QUERY } from '../../../clients/gql';

interface ContactOverviewFiltersProps {
  filters: ContactOverviewReportFiltersInput;
  onFilterChange: (field: keyof ContactOverviewReportFiltersInput, value: any) => void;
}

const contactTypeOptions = [
  { value: 'INFO', labelKey: 'comment-contact-type-info' },
  { value: 'PERS', labelKey: 'comment-contact-type-personal' },
  { value: 'TEL', labelKey: 'comment-contact-type-phone' },
  { value: 'EMAIL', labelKey: 'comment-contact-type-email' },
];

export default function ContactOverviewFilters({ filters, onFilterChange }: ContactOverviewFiltersProps) {
  // Mitarbeiter (Users) - seminargo users only
  const { data: usersData } = useQuery(LIST_KEY_ACCOUNT_MANAGERS_QUERY);
  const allUsers = usersData?.listKeyAccountManagers || [];

  // Companies 
  const [companySearchInput, setCompanySearchInput] = useState<string>('');
  const [selectedCompanies, setSelectedCompanies] = useState<typeof allCompanies>([]);
  const [runListCompanies, { data: companiesData, loading: companiesLoading }] = useLazyQuery(REPORT_LIST_PUBLIC_COMPANIES_QUERY, {
    fetchPolicy: 'network-only',
  });
  const allCompanies = companiesData?.reportListPublicCompanies || [];

  const companyOptions = React.useMemo(() => {
    if (selectedCompanies.length === 0) return allCompanies;
    const selectedIds = selectedCompanies.map(c => c.id);
    const newCompanies = allCompanies.filter(c => !selectedIds.includes(c.id));
    return [...selectedCompanies, ...newCompanies];
  }, [allCompanies, selectedCompanies]);

  useEffect(() => {
    if (filters.companyIds && filters.companyIds.length > 0 && selectedCompanies.length === 0) {
      const foundCompanies = allCompanies.filter(company => filters.companyIds?.includes(company.id));
      if (foundCompanies.length > 0) {
        setSelectedCompanies(foundCompanies);
      }
    } else if ((!filters.companyIds || filters.companyIds.length === 0) && selectedCompanies.length > 0) {
      setSelectedCompanies([]);
      setCompanySearchInput('');
    }
  }, [filters.companyIds, allCompanies, selectedCompanies.length]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (companySearchInput && companySearchInput.length >= 3) {
        runListCompanies({
          variables: {
            name: companySearchInput,
            country: filters.companyCountry,
          },
        });
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [companySearchInput, filters.companyCountry, runListCompanies]);

  // Contact Subjects
  const { data: contactSubjectsData } = useQuery(LIST_CONTACT_SUBJECTS_QUERY, {
    variables: {
      where: { isActive: true },
    },
  });
  const contactSubjects = contactSubjectsData?.listContactSubjects || [];

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={1.5}>
        {/* Date Range */}
        <Grid item xs={6} sm={4}>
          <SimpleDatePicker
            label={i18next.t('filter-date-from')}
            value={filters.dateFrom || null}
            onChange={(value) => onFilterChange('dateFrom', value)}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <SimpleDatePicker
            label={i18next.t('filter-date-to')}
            value={filters.dateTo || null}
            onChange={(value) => onFilterChange('dateTo', value)}
          />
        </Grid>

        {/* Mitarbeiter - Multiple Select */}
        <Grid item xs={6} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{i18next.t('filter-employee')}</InputLabel>
            <Select
              multiple
              value={filters.employeeIds || []}
              onChange={(e) => onFilterChange('employeeIds', e.target.value)}
              input={<OutlinedInput label={i18next.t('filter-employee')} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id: number) => {
                    const user = allUsers.find((u: any) => u.id === id);
                    return <Chip key={id} label={user?.name || `ID ${id}`} size="small" />;
                  })}
                </Box>
              )}
            >
              {allUsers.map((user: any) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Firma - Multiple Select with Search */}
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            multiple
            size="small"
            options={companyOptions}
            getOptionLabel={(option) => option.name || ''}
            value={selectedCompanies}
            onChange={(_, newValue) => {
              setSelectedCompanies(newValue);
              onFilterChange(
                'companyIds',
                newValue.map((v) => v.id)
              );
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setCompanySearchInput(newInputValue);
              }
            }}
            loading={companiesLoading}
            renderInput={(params) => (
              <TextField {...params} label={i18next.t('filter-company') || ''} placeholder={i18next.t('filter-company-search-placeholder') || ''} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                />
              ))
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>

        {/* Firmenland */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{i18next.t('filter-company-country')}</InputLabel>
            <Select
              value={filters.companyCountry || ''}
              onChange={(e) => onFilterChange('companyCountry', e.target.value || undefined)}
              label={i18next.t('filter-company-country')}
            >
              <MenuItem value="">
                <em>{i18next.t('filter-all')}</em>
              </MenuItem>
              {allCountriesSorted.map((country) => {
                if (!country) return <MenuItem key="divider" disabled>──────────</MenuItem>;
                return (
                  <MenuItem key={country.code} value={country.code}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{country.emoji}</span>
                      <span>{country.name}</span>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>

        {/* Betreff */}
        <Grid item xs={6} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{i18next.t('filter-contact-subject')}</InputLabel>
            <Select
              multiple
              value={filters.contactSubjectIds || []}
              onChange={(e) => {
                const value = e.target.value as number[] | string[];
                const normalized = value.map((v) => Number(v)).filter((v) => !Number.isNaN(v));
                onFilterChange('contactSubjectIds', normalized.length > 0 ? normalized : undefined);
              }}
              input={<OutlinedInput label={i18next.t('filter-contact-subject')} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {((selected as number[]).map((id) => {
                    const subject = contactSubjects.find((s) => s.id === id);
                    return <Chip key={id} label={subject?.name || `ID ${id}`} size="small" />;
                  }))}
                </Box>
              )}
            >
              {contactSubjects.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Kontaktart */}
        <Grid item xs={6} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{i18next.t('filter-contact-type')}</InputLabel>
            <Select
              multiple
              value={filters.contactTypes || []}
              onChange={(e) => {
                const value = e.target.value as string[] | number[];
                const normalized = value.map((v) => String(v));
                onFilterChange('contactTypes', normalized.length > 0 ? normalized : undefined);
              }}
              input={<OutlinedInput label={i18next.t('filter-contact-type')} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((typeValue) => {
                    const option = contactTypeOptions.find((o) => o.value === typeValue);
                    return <Chip key={typeValue} label={option ? String(i18next.t(option.labelKey)) : typeValue} size="small" />;
                  })}
                </Box>
              )}
            >
              {contactTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {i18next.t(option.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}